
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MessageSquare, LogOut, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { SidebarHeader as CustomSidebarHeader } from "./layout/sidebar/sidebar-header";
import { GuidelinesDialog } from "./layout/sidebar/guidelines-dialog";
import { AboutKlaraDialog } from "./layout/sidebar/about-klara-dialog";
import { AILogo } from "./layout/sidebar/ai-logo";
import { AdminButton } from "@/components/admin/admin-button";
import { ArchitectureButton } from "./layout/sidebar/architecture-button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { MobileSidebar } from "@/components/ui/mobile-sidebar";

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isMobile, openMobile, setOpenMobile } = useSidebar();
  const [guidelinesOpen, setGuidelinesOpen] = React.useState(false);
  const [aboutKlaraOpen, setAboutKlaraOpen] = React.useState(false);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Logget ut",
        description: "Du har blitt logget ut av systemet",
      });
      navigate("/auth");
    } catch (error) {
      toast({
        title: "Feil ved utlogging",
        description: "Kunne ikke logge ut. Prøv igjen senere.",
        variant: "destructive",
      });
    }
  };

  // Mobile sidebar content - maximized scrollable area
  const mobileContent = (
    <div className="flex h-full flex-col">
      {/* Header - minimal padding */}
      <div className="p-2 border-b border-sidebar-border flex-shrink-0">
        <CustomSidebarHeader />
      </div>
      
      {/* Scrollable content - maximized space */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
        {/* Navigation */}
        <div>
          <h3 className="text-sm font-medium text-sidebar-foreground/70 mb-2">Navigasjon</h3>
          <div className="space-y-1">
            <Button
              variant="ghost"
              className={`w-full justify-start gap-2 h-12 ${
                location.pathname === "/" ? "bg-sidebar-accent text-sidebar-accent-foreground" : ""
              }`}
              onClick={() => navigate("/")}
            >
              <MessageSquare className="h-5 w-5" />
              <span>Chat</span>
            </Button>
            <Button
              variant="ghost"
              className={`w-full justify-start gap-2 h-12 ${
                location.pathname === "/settings" ? "bg-sidebar-accent text-sidebar-accent-foreground" : ""
              }`}
              onClick={() => navigate("/settings")}
            >
              <Settings className="h-5 w-5" />
              <span>Innstillinger</span>
            </Button>
          </div>
        </div>

        {/* Settings */}
        <div>
          <div className="space-y-1">
            <GuidelinesDialog
              open={guidelinesOpen}
              onOpenChange={setGuidelinesOpen}
            />
            <AboutKlaraDialog
              open={aboutKlaraOpen}
              onOpenChange={setAboutKlaraOpen}
            />
            <ArchitectureButton />
            <AdminButton />
          </div>
        </div>
      </div>

      {/* Footer - very compact */}
      <div className="flex-shrink-0 border-t border-sidebar-border">
        <div className="p-2">
          <AILogo />
        </div>
        
        <div className="p-2">
          <img
            src="/lovable-uploads/c84656a1-7114-4135-b66e-5197a7e80819.png"
            alt="Teknologisk illustrasjon av arbeidsinkludering"
            className="w-[70%] h-auto mx-auto rounded-lg"
          />
        </div>
        
        <div className="p-2">
          <Button 
            variant="ghost" 
            className="w-full justify-start gap-2 h-12" 
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5" />
            <span>Logg ut</span>
          </Button>
        </div>
      </div>
    </div>
  );

  // If mobile, return the custom mobile sidebar directly
  if (isMobile) {
    return (
      <MobileSidebar 
        open={openMobile} 
        onOpenChange={setOpenMobile}
        className="bg-sidebar text-sidebar-foreground"
      >
        {mobileContent}
      </MobileSidebar>
    );
  }

  // Desktop sidebar - keep existing structure
  return (
    <Sidebar>
      <SidebarHeader className="flex-shrink-0">
        <CustomSidebarHeader />
      </SidebarHeader>
      
      <SidebarContent className="flex-1 min-h-0 overflow-hidden">
        <div className="h-full overflow-auto px-2">
          <div className="space-y-4 py-2">
            <SidebarGroup>
              <SidebarGroupLabel>Navigasjon</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={location.pathname === "/"}>
                      <a href="/">
                        <MessageSquare className="h-5 w-5" />
                        <span>Chat</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={location.pathname === "/settings"}>
                      <a href="/settings">
                        <Settings className="h-5 w-5" />
                        <span>Innstillinger</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <GuidelinesDialog
                      open={guidelinesOpen}
                      onOpenChange={setGuidelinesOpen}
                    />
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <AboutKlaraDialog
                      open={aboutKlaraOpen}
                      onOpenChange={setAboutKlaraOpen}
                    />
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <ArchitectureButton />
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <AdminButton />
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </div>
        </div>
      </SidebarContent>

      <SidebarFooter className="flex-shrink-0 mt-auto">
        <AILogo />
        
        <div className="p-3">
          <img
            src="/lovable-uploads/c84656a1-7114-4135-b66e-5197a7e80819.png"
            alt="Teknologisk illustrasjon av arbeidsinkludering"
            className="w-[90%] h-auto mx-auto rounded-lg"
          />
        </div>
        
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleLogout}>
              <LogOut className="h-5 w-5" />
              <span>Logg ut</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
